import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Headphones, Loader, BookOpen, BarChart2, Flame, TrendingUp, Award } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { egwEnrollmentService, egwContentService, egwAudioService } from '../../../services/egwPlanService';
import { useAudioPlayerContext } from '../../../contexts/AudioPlayerContext';
import { useAwardToast } from '../../../contexts/AwardToastContext';
import { supabase } from '../../../lib/supabase';

// ── Award helpers ─────────────────────────────────────────────────────────────
async function grantEgwDayAward(userId) {
  try {
    const { data: bal } = await supabase?.from('user_talent_balance')?.select('gold_balance, total_gold_earned')?.eq('user_id', userId)?.maybeSingle();

    let newGold = (bal?.gold_balance || 0) + 5;
    const newTotal = (bal?.total_gold_earned || 0) + 5;

    await supabase?.from('user_talent_balance')?.upsert(
        { user_id: userId, gold_balance: newGold, total_gold_earned: newTotal, updated_at: new Date()?.toISOString() },
        { onConflict: 'user_id' }
      );
    return { gold: 5 };
  } catch (err) {
    console.error('grantEgwDayAward error:', err);
    return null;
  }
}

async function grantEgwPlanCompleteAward(userId) {
  try {
    const { data: bal } = await supabase?.from('user_talent_balance')?.select('silver_balance, gold_balance, total_silver_earned, total_gold_earned')?.eq('user_id', userId)?.maybeSingle();

    let newSilver = (bal?.silver_balance || 0) + 1;
    let newGold = bal?.gold_balance || 0;
    let totalSilver = (bal?.total_silver_earned || 0) + 1;
    let totalGold = bal?.total_gold_earned || 0;

    if (newSilver >= 7) {
      newGold += Math.floor(newSilver / 7);
      totalGold += Math.floor(newSilver / 7);
      newSilver = newSilver % 7;
    }

    await supabase?.from('user_talent_balance')?.upsert(
        { user_id: userId, silver_balance: newSilver, gold_balance: newGold, total_silver_earned: totalSilver, total_gold_earned: totalGold, updated_at: new Date()?.toISOString() },
        { onConflict: 'user_id' }
      );

    // Grant badge if award definition exists
    const { data: def } = await supabase?.from('award_definitions')?.select('slug, name')?.eq('trigger_event', 'READING_PLAN_COMPLETED')?.maybeSingle();

    if (def?.slug) {
      await supabase?.from('user_awards')?.upsert(
          { user_id: userId, award_slug: def?.slug, times_earned: 1, earned_at: new Date()?.toISOString() },
          { onConflict: 'user_id,award_slug' }
        );
    }

    return { silver: 1, badge: def?.name || 'EGW Plan Complete' };
  } catch (err) {
    console.error('grantEgwPlanCompleteAward error:', err);
    return null;
  }
}

// ── Analytics fetcher ─────────────────────────────────────────────────────────
async function fetchReadingAnalytics(userId, planId, enrollment, planMeta) {
  try {
    const { data: completions } = await supabase?.from('egw_user_day_completions')?.select('day_number, completed_at')?.eq('user_id', userId)?.eq('plan_id', planId)?.order('completed_at', { ascending: true });

    const completedDays = completions?.length || 0;
    const pagesRead = completedDays * (enrollment?.pace_pages_per_day || 3);

    // Streak: consecutive days ending today
    let streak = 0;
    if (completions?.length) {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const dates = completions?.map(c => { const d = new Date(c.completed_at); d?.setHours(0, 0, 0, 0); return d?.getTime(); })?.sort((a, b) => b - a);
      const DAY = 86400000;
      let check = today?.getTime();
      for (const d of dates) {
        if (d === check || d === check - DAY) { streak++; check = d - DAY; }
        else break;
      }
    }

    // On track
    const startDate = enrollment?.created_at ? new Date(enrollment.created_at) : null;
    let onTrack = true;
    if (startDate) {
      const elapsed = Math.floor((Date.now() - startDate?.getTime()) / 86400000);
      const expected = Math.min(elapsed + 1, planMeta?.total_days || 1);
      onTrack = completedDays >= expected;
    }

    return { completedDays, pagesRead, streak, onTrack };
  } catch {
    return { completedDays: 0, pagesRead: 0, streak: 0, onTrack: true };
  }
}

// ── Chapter fetcher ───────────────────────────────────────────────────────────
async function fetchBookChapters(bookCode) {
  try {
    const { data } = await supabase?.from('egw_content')?.select('chapter_title, page_number')?.eq('book_code', bookCode)?.order('page_number', { ascending: true });

    const seen = new Set();
    const chapters = [];
    for (const row of (data || [])) {
      const key = row?.chapter_title || `Page ${row?.page_number}`;
      if (!seen?.has(key)) { seen?.add(key); chapters?.push({ title: key, page: row?.page_number }); }
    }
    return chapters;
  } catch {
    return [];
  }
}

// ── Page Turn Animation Component ─────────────────────────────────────────────
const PageTurnOverlay = ({ direction, isAnimating }) => {
  if (!isAnimating) return null;
  return (
    <div
      className="absolute inset-0 pointer-events-none z-20 overflow-hidden rounded-xl"
      style={{ perspective: '1200px' }}
    >
      <div
        className="absolute inset-0 rounded-xl"
        style={{
          background: direction === 'next' ?'linear-gradient(to left, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)' :'linear-gradient(to right, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)',
          animation: direction === 'next' ? 'pageTurnNext 0.45s ease-in-out forwards' : 'pageTurnPrev 0.45s ease-in-out forwards',
        }}
      />
      <div
        className="absolute top-0 bottom-0 w-16"
        style={{
          right: direction === 'next' ? 0 : 'auto',
          left: direction === 'prev' ? 0 : 'auto',
          background: direction === 'next' ?'linear-gradient(to left, rgba(0,0,0,0.12), transparent)' :'linear-gradient(to right, rgba(0,0,0,0.12), transparent)',
          animation: direction === 'next' ? 'shadowSweepNext 0.45s ease-in-out forwards' : 'shadowSweepPrev 0.45s ease-in-out forwards',
        }}
      />
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
const EGWReadingInterface = ({ enrollment, plan, onDayComplete, onBack }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Pagination state
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [animDirection, setAnimDirection] = useState('next');
  const [slideClass, setSlideClass] = useState('');
  const contentRef = useRef(null);

  const audioPlayerContext = useAudioPlayerContext();
  const { openEgwPlayer } = audioPlayerContext || {};
  const { showAwardToast } = useAwardToast();

  const currentDay = enrollment?.current_day || 1;
  const planMeta = plan?.egw_reading_plans || plan;
  const totalDays = planMeta?.total_days || 1;
  const progressPct = Math.min(Math.round(((currentDay - 1) / Math.max(totalDays, 1)) * 100), 100);
  const bookCode = planMeta?.book_code;

  useEffect(() => { loadDayContent(); }, [enrollment?.plan_id, currentDay]);

  useEffect(() => {
    if (enrollment?.user_id && enrollment?.plan_id) loadAnalytics();
  }, [enrollment?.user_id, enrollment?.plan_id, completed]);

  useEffect(() => {
    if (bookCode) fetchBookChapters(bookCode)?.then(setChapters);
  }, [bookCode]);

  // Reset page index when content changes
  useEffect(() => {
    setCurrentPageIndex(0);
  }, [content]);

  const loadDayContent = async () => {
    if (!enrollment?.plan_id) return;
    setLoading(true);
    const { data, error } = await egwContentService?.getDayContent(enrollment?.plan_id, currentDay, bookCode);
    if (error) {
      console.error('EGW getDayContent error:', error);
    }
    setContent(data || null);
    setLoading(false);
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    const result = await fetchReadingAnalytics(enrollment?.user_id, enrollment?.plan_id, enrollment, planMeta);
    setAnalytics(result);
    setAnalyticsLoading(false);
  };

  const handleMarkComplete = async () => {
    setCompleting(true);
    try {
      const { error } = await egwEnrollmentService?.markDayComplete(
        enrollment?.user_id,
        enrollment?.plan_id,
        currentDay,
        enrollment?.id
      );
      if (!error) {
        // Award 5 gold for day completion
        const dayAward = await grantEgwDayAward(enrollment?.user_id);
        if (dayAward) {
          showAwardToast({
            name: 'Daily Reading Complete',
            description: `Day ${currentDay} finished — +${dayAward?.gold} Gold`,
            icon: '📖',
            goldReward: dayAward?.gold,
          });
        }

        // Full plan complete?
        if (currentDay >= totalDays) {
          const planAward = await grantEgwPlanCompleteAward(enrollment?.user_id);
          if (planAward) {
            setTimeout(() => {
              showAwardToast({
                name: planAward?.badge,
                description: 'Plan complete — +1 Silver Talent',
                icon: '🏆',
                silverReward: 1,
              });
            }, 2500);
          }
        }

        setCompleted(true);
        if (onDayComplete) onDayComplete(currentDay);
      }
    } catch (err) {
      console.error('Error marking day complete:', err);
    } finally {
      setCompleting(false);
    }
  };

  const handleOpenAudio = async () => {
    if (!content?.paragraphs?.length) return;
    const bc = content?.paragraphs?.[0]?.book_code;
    if (!bc) return;
    const { data: audioFile } = await egwAudioService?.getAudioFile(bc, 1, 'en-US-Neural2-F');
    if (audioFile && openEgwPlayer) openEgwPlayer(bc, audioFile?.sequence_number, 'en-US-Neural2-F');
  };

  // Group paragraphs by page
  const pageGroups = {};
  for (const para of (content?.paragraphs || [])) {
    const pg = para?.page_number;
    if (!pageGroups?.[pg]) pageGroups[pg] = [];
    pageGroups?.[pg]?.push(para);
  }
  const sortedPages = Object.keys(pageGroups)?.map(Number)?.sort((a, b) => a - b);
  const totalReaderPages = sortedPages?.length;

  // Page navigation with animation
  const goToPage = (newIndex, direction) => {
    if (animating || newIndex < 0 || newIndex >= totalReaderPages) return;
    setAnimDirection(direction);
    setAnimating(true);
    setSlideClass(direction === 'next' ? 'slide-out-left' : 'slide-out-right');

    setTimeout(() => {
      setCurrentPageIndex(newIndex);
      setSlideClass(direction === 'next' ? 'slide-in-right' : 'slide-in-left');
      if (contentRef?.current) contentRef.current.scrollTop = 0;
      setTimeout(() => {
        setSlideClass('');
        setAnimating(false);
      }, 50);
    }, 300);
  };

  const goNext = () => goToPage(currentPageIndex + 1, 'next');
  const goPrev = () => goToPage(currentPageIndex - 1, 'prev');

  const pageProgressPct = totalReaderPages > 1
    ? Math.round((currentPageIndex / (totalReaderPages - 1)) * 100)
    : 100;

  const currentPageNum = sortedPages?.[currentPageIndex];
  const currentParas = pageGroups?.[currentPageNum] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!content || (!content?.paragraphs?.length && !content?.pages?.length)) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No content found for Day {currentDay}</p>
        <p className="text-xs text-muted-foreground mt-1">Plan ID: {enrollment?.plan_id} · Book: {bookCode || 'unknown'}</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">← Back to Plans</Button>
      </div>
    );
  }

  return (
    <>
      {/* Inject keyframe animations */}
      <style>{`
        @keyframes pageTurnNext {
          0% { opacity: 0; transform: translateX(8px); }
          100% { opacity: 0; transform: translateX(0); }
        }
        @keyframes pageTurnPrev {
          0% { opacity: 0; transform: translateX(-8px); }
          100% { opacity: 0; transform: translateX(0); }
        }
        .slide-out-left {
          animation: slideOutLeft 0.3s ease-in-out forwards;
        }
        .slide-out-right {
          animation: slideOutRight 0.3s ease-in-out forwards;
        }
        .slide-in-right {
          animation: slideInRight 0.01s forwards;
          opacity: 0;
        }
        .slide-in-left {
          animation: slideInLeft 0.01s forwards;
          opacity: 0;
        }
        @keyframes slideOutLeft {
          0% { opacity: 1; transform: translateX(0) rotateY(0deg); }
          100% { opacity: 0; transform: translateX(-40px) rotateY(8deg); }
        }
        @keyframes slideOutRight {
          0% { opacity: 1; transform: translateX(0) rotateY(0deg); }
          100% { opacity: 0; transform: translateX(40px) rotateY(-8deg); }
        }
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(40px) rotateY(-8deg); }
          100% { opacity: 1; transform: translateX(0) rotateY(0deg); }
        }
        @keyframes slideInLeft {
          0% { opacity: 0; transform: translateX(-40px) rotateY(8deg); }
          100% { opacity: 1; transform: translateX(0) rotateY(0deg); }
        }
        .page-content-enter {
          animation: pageEnter 0.35s ease-out forwards;
        }
        @keyframes pageEnter {
          0% { opacity: 0; transform: translateX(var(--enter-from, 40px)); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div className="max-w-3xl mx-auto">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft size={16} />
            Back to Plans
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAnalytics(v => !v)}
              className={`flex items-center gap-1.5 text-sm transition-colors ${showAnalytics ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <BarChart2 size={16} />
              Analytics
            </button>
            <button
              onClick={handleOpenAudio}
              className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <Headphones size={16} />
              Audio
            </button>
          </div>
        </div>

        {/* Overall Plan Progress Bar */}
        <div className="mb-4 bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen size={14} className="text-primary" />
              <span className="text-sm font-medium text-foreground line-clamp-1">{planMeta?.title}</span>
            </div>
            <span className="text-xs font-bold text-primary">{progressPct}%</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
            <span>Day {currentDay} of {totalDays}</span>
            <span>{Math.max(totalDays - currentDay, 0)} days remaining</span>
          </div>
        </div>

        {/* Book/Chapter Selector */}
        {chapters?.length > 0 && (
          <div className="mb-4">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Jump to Chapter</label>
            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e?.target?.value)}
              className="w-full text-sm bg-card border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">— Select a chapter —</option>
              {chapters?.map((ch, i) => (
                <option key={i} value={ch?.page}>{ch?.title}</option>
              ))}
            </select>
          </div>
        )}

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-5 bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <TrendingUp size={14} className="text-primary" />
              Reading Analytics
            </h3>
            {analyticsLoading ? (
              <div className="flex justify-center py-4">
                <Loader size={18} className="animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{analytics?.pagesRead ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Pages Read</div>
                </div>
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground">{analytics?.completedDays ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Days Done</div>
                </div>
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${analytics?.onTrack ? 'text-green-500' : 'text-yellow-500'}`}>
                    {analytics?.onTrack ? '✓' : '↓'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{analytics?.onTrack ? 'On Track' : 'Behind'}</div>
                </div>
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-foreground flex items-center justify-center gap-1">
                    <Flame size={18} className="text-orange-400" />
                    {analytics?.streak ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Day Streak</div>
                </div>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground bg-primary/5 rounded-lg px-3 py-2">
              <Award size={12} className="text-primary shrink-0" />
              <span>
                Complete each day → <strong className="text-foreground">+5 Gold 🪙</strong>.
                Finish the full plan → <strong className="text-foreground">+1 Silver 🥈 + Achievement Badge 🏆</strong>.
              </span>
            </div>
          </div>
        )}

        {/* Day info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              Day {currentDay}
            </span>
            <span className="text-xs text-muted-foreground">{planMeta?.title}</span>
          </div>
          {content?.day?.title && (
            <h2 className="text-xl font-heading font-bold text-foreground">{content?.day?.title}</h2>
          )}
        </div>

        {/* Page Progress Bar (within day) */}
        {totalReaderPages > 1 && (
          <div className="mb-4 bg-card border border-border rounded-xl px-4 py-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-muted-foreground">Today's Reading</span>
              <span className="text-xs font-bold text-primary">
                Page {currentPageIndex + 1} of {totalReaderPages}
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent to-primary rounded-full transition-all duration-500"
                style={{ width: `${pageProgressPct}%` }}
              />
            </div>
            {/* Page dot indicators (up to 12 dots) */}
            {totalReaderPages <= 12 && (
              <div className="flex items-center justify-center gap-1.5 mt-2">
                {sortedPages?.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => !animating && goToPage(idx, idx > currentPageIndex ? 'next' : 'prev')}
                    className={`rounded-full transition-all duration-300 ${
                      idx === currentPageIndex
                        ? 'w-4 h-2 bg-primary'
                        : idx < currentPageIndex
                        ? 'w-2 h-2 bg-primary/50' :'w-2 h-2 bg-muted-foreground/30'
                    }`}
                    aria-label={`Go to page ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Card with Page Turn */}
        <div className="relative mb-4" style={{ perspective: '1200px' }}>
          <div
            ref={contentRef}
            className={`bg-card border border-border rounded-xl p-6 overflow-hidden transition-all ${slideClass}`}
            style={{ minHeight: '300px', transformStyle: 'preserve-3d' }}
          >
            <PageTurnOverlay direction={animDirection} isAnimating={animating} />

            {/* Page header */}
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">
                  p. {currentPageNum}
                </span>
                {content?.day?.title && (
                  <span className="text-xs text-muted-foreground hidden sm:inline">{content?.day?.title}</span>
                )}
              </div>
              <span className="text-xs text-muted-foreground font-mono">
                {currentPageIndex + 1} / {totalReaderPages}
              </span>
            </div>

            {/* Paragraphs */}
            <div className="space-y-4">
              {currentParas?.map((para, paraIdx) => {
                // Build paragraph code: BOOKCODE.PAGE.PARA (e.g. DA.123.2)
                const paraCode = `${(para?.book_code || bookCode || '')?.toUpperCase()}.${para?.page_number}.${paraIdx + 1}`;
                return (
                  <div key={para?.content_sequence_id} className="relative group">
                    <p
                      className="text-foreground leading-relaxed text-base"
                      dangerouslySetInnerHTML={{ __html: para?.content }}
                    />
                    <span className="inline-block mt-1 text-[10px] font-mono text-muted-foreground/50 select-all group-hover:text-muted-foreground/80 transition-colors">
                      {paraCode}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Page footer */}
            <div className="mt-6 pt-3 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground/50 font-mono">— {planMeta?.title} —</span>
              <span className="text-xs font-mono text-muted-foreground/50">p. {currentPageNum}</span>
            </div>
          </div>
        </div>

        {/* Page Navigation Controls */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goPrev}
            disabled={currentPageIndex === 0 || animating}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 ${
              currentPageIndex === 0 || animating
                ? 'border-border text-muted-foreground/40 cursor-not-allowed'
                : 'border-border text-foreground hover:bg-muted hover:border-primary/30 active:scale-95'
            }`}
          >
            <ChevronLeft size={16} />
            Previous Page
          </button>

          {/* Center: page fraction */}
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-sm font-semibold text-foreground">
              {currentPageIndex + 1} / {totalReaderPages}
            </span>
            <span className="text-xs text-muted-foreground">pages today</span>
          </div>

          {currentPageIndex < totalReaderPages - 1 ? (
            <button
              onClick={goNext}
              disabled={animating}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 ${
                animating
                  ? 'border-border text-muted-foreground/40 cursor-not-allowed'
                  : 'border-primary bg-primary/5 text-primary hover:bg-primary/10 active:scale-95'
              }`}
            >
              Next Page
              <ChevronRight size={16} />
            </button>
          ) : (
            (<div className="w-32" />) /* spacer when on last page */
          )}
        </div>

        {/* Mark Complete — only shown on last page */}
        {currentPageIndex === totalReaderPages - 1 && (
          <div className="flex flex-col items-center gap-2 pb-8">
            {completed ? (
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-2 text-green-500 font-semibold">
                  <CheckCircle size={20} />
                  Day {currentDay} Complete!
                </div>
                <span className="text-xs text-muted-foreground">+5 Gold awarded 🪙</span>
              </div>
            ) : (
              <>
                <Button onClick={handleMarkComplete} disabled={completing} className="flex items-center gap-2 px-8">
                  {completing ? (
                    <><Loader className="w-4 h-4 animate-spin" />Saving...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" />Mark Day Complete</>
                  )}
                </Button>
                <span className="text-xs text-muted-foreground">Earn 5 Gold 🪙 for completing today's reading</span>
              </>
            )}
          </div>
        )}

        {/* Hint when not on last page */}
        {currentPageIndex < totalReaderPages - 1 && (
          <div className="flex justify-center pb-8">
            <span className="text-xs text-muted-foreground italic">
              Read all {totalReaderPages} pages to mark this day complete
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default EGWReadingInterface;
